import type { ShopTemplateBlock } from "./types";

export const SHOP_COMMERCE_RUNTIME_ROLE_OPTION = "commerceRuntimeRole";

export const SHOP_COMMERCE_RUNTIME_ROLES = {
    accountNavigation: "commerce.account.navigation",
    cartItems: "commerce.cart.items",
    cartSummary: "commerce.cart.summary",
    catalogProducts: "commerce.catalog.products",
    catalogSubcategories: "commerce.catalog.subcategories",
    checkoutSteps: "commerce.checkout.steps",
    contactForm: "commerce.contact.form",
    productBuyBox: "commerce.product.buyBox",
    productDescription: "commerce.product.description",
    productGallery: "commerce.product.gallery",
    productSummary: "commerce.product.summary",
    searchForm: "commerce.search.form",
} as const;

export type ShopCommerceRuntimeRole = typeof SHOP_COMMERCE_RUNTIME_ROLES[keyof typeof SHOP_COMMERCE_RUNTIME_ROLES];

const allRuntimeRoles = new Set<ShopCommerceRuntimeRole>(Object.values(SHOP_COMMERCE_RUNTIME_ROLES));

const legacyBlockRoles: Readonly<Record<string, ShopCommerceRuntimeRole>> = {
    systemAccountNavigation: SHOP_COMMERCE_RUNTIME_ROLES.accountNavigation,
    systemCartItems: SHOP_COMMERCE_RUNTIME_ROLES.cartItems,
    systemCartSummary: SHOP_COMMERCE_RUNTIME_ROLES.cartSummary,
    systemCheckoutSteps: SHOP_COMMERCE_RUNTIME_ROLES.checkoutSteps,
    systemContactForm: SHOP_COMMERCE_RUNTIME_ROLES.contactForm,
    systemProductBuyBox: SHOP_COMMERCE_RUNTIME_ROLES.productBuyBox,
    systemProductDescription: SHOP_COMMERCE_RUNTIME_ROLES.productDescription,
    systemProductGallery: SHOP_COMMERCE_RUNTIME_ROLES.productGallery,
    systemProductList: SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts,
    systemProductSummary: SHOP_COMMERCE_RUNTIME_ROLES.productSummary,
    systemSearchForm: SHOP_COMMERCE_RUNTIME_ROLES.searchForm,
    systemSubcategoryList: SHOP_COMMERCE_RUNTIME_ROLES.catalogSubcategories,
};

const morrowRuntimeSourceRoles: Readonly<Record<string, ShopCommerceRuntimeRole>> = {
    accountSystem: SHOP_COMMERCE_RUNTIME_ROLES.accountNavigation,
    cartItemsSystem: SHOP_COMMERCE_RUNTIME_ROLES.cartItems,
    cartSummarySystem: SHOP_COMMERCE_RUNTIME_ROLES.cartSummary,
    catalogProductsSystem: SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts,
    catalogSubcategoriesSystem: SHOP_COMMERCE_RUNTIME_ROLES.catalogSubcategories,
    categoriesSystem: SHOP_COMMERCE_RUNTIME_ROLES.catalogSubcategories,
    checkoutStepsSystem: SHOP_COMMERCE_RUNTIME_ROLES.checkoutSteps,
    checkoutSummarySystem: SHOP_COMMERCE_RUNTIME_ROLES.cartSummary,
    contactFormSystem: SHOP_COMMERCE_RUNTIME_ROLES.contactForm,
    productBuyBoxSystem: SHOP_COMMERCE_RUNTIME_ROLES.productBuyBox,
    productDescriptionSystem: SHOP_COMMERCE_RUNTIME_ROLES.productDescription,
    productFeedSystem: SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts,
    productGallerySystem: SHOP_COMMERCE_RUNTIME_ROLES.productGallery,
    productSummarySystem: SHOP_COMMERCE_RUNTIME_ROLES.productSummary,
    recommendationsSystem: SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts,
    searchResultsSystem: SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts,
};

const morrowRegionRoles: Readonly<Record<string, readonly ShopCommerceRuntimeRole[]>> = {
    account: [SHOP_COMMERCE_RUNTIME_ROLES.accountNavigation],
    cart: [SHOP_COMMERCE_RUNTIME_ROLES.cartItems, SHOP_COMMERCE_RUNTIME_ROLES.cartSummary],
    catalog: [SHOP_COMMERCE_RUNTIME_ROLES.catalogSubcategories, SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts],
    checkout: [SHOP_COMMERCE_RUNTIME_ROLES.checkoutSteps, SHOP_COMMERCE_RUNTIME_ROLES.cartSummary],
    contactForm: [SHOP_COMMERCE_RUNTIME_ROLES.contactForm],
    product: [
        SHOP_COMMERCE_RUNTIME_ROLES.productGallery,
        SHOP_COMMERCE_RUNTIME_ROLES.productSummary,
        SHOP_COMMERCE_RUNTIME_ROLES.productBuyBox,
        SHOP_COMMERCE_RUNTIME_ROLES.productDescription,
    ],
    search: [SHOP_COMMERCE_RUNTIME_ROLES.searchForm],
    searchResults: [SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts],
};

const requiredRolesByPageType: Readonly<Record<string, readonly ShopCommerceRuntimeRole[]>> = {
    cart: [SHOP_COMMERCE_RUNTIME_ROLES.cartItems, SHOP_COMMERCE_RUNTIME_ROLES.cartSummary],
    category: [SHOP_COMMERCE_RUNTIME_ROLES.catalogSubcategories, SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts],
    checkout: [SHOP_COMMERCE_RUNTIME_ROLES.checkoutSteps, SHOP_COMMERCE_RUNTIME_ROLES.cartSummary],
    contact: [SHOP_COMMERCE_RUNTIME_ROLES.contactForm],
    customerAccount: [SHOP_COMMERCE_RUNTIME_ROLES.accountNavigation],
    product: [
        SHOP_COMMERCE_RUNTIME_ROLES.productGallery,
        SHOP_COMMERCE_RUNTIME_ROLES.productSummary,
        SHOP_COMMERCE_RUNTIME_ROLES.productBuyBox,
        SHOP_COMMERCE_RUNTIME_ROLES.productDescription,
    ],
    search: [SHOP_COMMERCE_RUNTIME_ROLES.searchForm, SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts],
    section: [SHOP_COMMERCE_RUNTIME_ROLES.catalogSubcategories, SHOP_COMMERCE_RUNTIME_ROLES.catalogProducts],
};

function optionText(block: ShopTemplateBlock, key: string): string {
    const value = block.options?.[key];

    return typeof value === "string" ? value.trim() : "";
}

function optionEnabled(block: ShopTemplateBlock, key: string): boolean {
    const value = block.options?.[key];

    return value === true || value === "true" || value === 1 || value === "1";
}

function runtimeRoleIsOptional(block: ShopTemplateBlock): boolean {
    return optionEnabled(block, "commerceRuntimeOptional");
}

function explicitRuntimeRoles(block: ShopTemplateBlock): ShopCommerceRuntimeRole[] {
    const value = block.options?.[SHOP_COMMERCE_RUNTIME_ROLE_OPTION];
    const candidates = Array.isArray(value) ? value : [value];

    return candidates.filter((candidate): candidate is ShopCommerceRuntimeRole => (
        typeof candidate === "string" && allRuntimeRoles.has(candidate as ShopCommerceRuntimeRole)
    ));
}

export function shopCommerceRequiredRuntimeRoles(pageType: string | null | undefined): readonly ShopCommerceRuntimeRole[] {
    return pageType ? requiredRolesByPageType[pageType] ?? [] : [];
}

/** Returns roles provided or hosted directly by this block, without visiting descendants. */
export function shopCommerceRuntimeRolesForBlock(block: ShopTemplateBlock | null | undefined): ShopCommerceRuntimeRole[] {
    if (!block) return [];

    const roles = new Set<ShopCommerceRuntimeRole>(explicitRuntimeRoles(block));
    const legacyRole = legacyBlockRoles[block.type];
    const sourceRole = morrowRuntimeSourceRoles[optionText(block, "runtimeSource")];
    const regionRoles = morrowRegionRoles[optionText(block, "morrowRegion")] ?? [];

    if (legacyRole) roles.add(legacyRole);
    if (sourceRole) roles.add(sourceRole);
    regionRoles.forEach((role) => roles.add(role));

    return Array.from(roles);
}

export function shopCommerceRuntimeRolesInBlocks(blocks: readonly ShopTemplateBlock[]): ShopCommerceRuntimeRole[] {
    const roles = new Set<ShopCommerceRuntimeRole>();
    const visit = (block: ShopTemplateBlock) => {
        shopCommerceRuntimeRolesForBlock(block).forEach((role) => roles.add(role));
        Object.values(block.slots ?? {}).flat().forEach(visit);
        block.children?.forEach(visit);
    };

    blocks.forEach(visit);

    return Array.from(roles);
}

export function shopCommerceMissingRuntimeRoles(
    blocks: readonly ShopTemplateBlock[],
    pageType: string | null | undefined,
): ShopCommerceRuntimeRole[] {
    const existing = new Set(shopCommerceRuntimeRolesInBlocks(blocks));

    return shopCommerceRequiredRuntimeRoles(pageType).filter((role) => !existing.has(role));
}

export function shopCommerceBlockIsRuntimeProtected(
    block: ShopTemplateBlock | null | undefined,
    pageType: string | null | undefined,
): boolean {
    if (block && runtimeRoleIsOptional(block)) return false;

    const required = new Set(shopCommerceRequiredRuntimeRoles(pageType));

    return shopCommerceRuntimeRolesForBlock(block).some((role) => required.has(role));
}

export function shopCommerceSubtreeHasRuntimeProtection(
    block: ShopTemplateBlock | null | undefined,
    pageType: string | null | undefined,
): boolean {
    if (!block) return false;
    if (shopCommerceBlockIsRuntimeProtected(block, pageType)) return true;

    return Object.values(block.slots ?? {}).flat().some((child) => (
        shopCommerceSubtreeHasRuntimeProtection(child, pageType)
    )) || (block.children ?? []).some((child) => shopCommerceSubtreeHasRuntimeProtection(child, pageType));
}

type ProtectedNode = {
    immutableSignature: string;
    location: string;
    roles: ShopCommerceRuntimeRole[];
    uuid: string;
};

function protectedNodes(
    blocks: readonly ShopTemplateBlock[],
    pageType: string | null | undefined,
): ProtectedNode[] {
    const required = new Set(shopCommerceRequiredRuntimeRoles(pageType));
    const result: ProtectedNode[] = [];

    const visit = (block: ShopTemplateBlock, location: string) => {
        const roles = shopCommerceRuntimeRolesForBlock(block).filter((role) => required.has(role));

        if (roles.length > 0 && block.uuid && !runtimeRoleIsOptional(block)) {
            result.push({
                immutableSignature: JSON.stringify({
                    commerceRuntimeRole: block.options?.[SHOP_COMMERCE_RUNTIME_ROLE_OPTION] ?? null,
                    documentRole: block.options?.documentRole ?? null,
                    morrowRegion: block.options?.morrowRegion ?? null,
                    runtimeSource: block.options?.runtimeSource ?? null,
                    type: block.type,
                }),
                location,
                roles,
                uuid: block.uuid,
            });
        }

        Object.entries(block.slots ?? {}).forEach(([slotKey, children]) => {
            children.forEach((child) => visit(child, `slot:${block.uuid ?? "anonymous"}:${slotKey}`));
        });
        block.children?.forEach((child) => visit(child, `children:${block.uuid ?? "anonymous"}`));
    };

    blocks.forEach((block) => visit(block, "root"));

    return result;
}

/**
 * Verifies an editor transition without freezing visual properties. Protected nodes may be
 * reordered, resized and styled, but cannot be removed, reparented or have their runtime identity changed.
 */
export function shopCommerceRuntimeTransitionIsSafe(
    previousBlocks: readonly ShopTemplateBlock[],
    nextBlocks: readonly ShopTemplateBlock[],
    pageType: string | null | undefined,
): boolean {
    const previousMissing = new Set(shopCommerceMissingRuntimeRoles(previousBlocks, pageType));
    const nextMissing = shopCommerceMissingRuntimeRoles(nextBlocks, pageType);

    if (nextMissing.some((role) => !previousMissing.has(role))) return false;

    const previousNodes = protectedNodes(previousBlocks, pageType);
    const nextNodes = protectedNodes(nextBlocks, pageType);
    const nextByUuid = new Map(nextNodes.map((node) => [node.uuid, node]));

    for (const previous of previousNodes) {
        const next = nextByUuid.get(previous.uuid);

        if (
            !next
            || next.immutableSignature !== previous.immutableSignature
            || next.location !== previous.location
        ) {
            return false;
        }
    }

    return true;
}
